import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  GrantsAndAllowedApis,
  IsolinesProviders,
  Language,
  NumericType,
  Provider,
  QueryAndOutputs,
  TableSchema,
  TokenAllowedApis,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentInput,
  WorkflowComponentParameterMode,
  WorkflowComponentParamType,
  WorkflowComponentValue,
  WorkflowEnvVars,
  WorkflowParameterValueErrorType
} from '../types'
import { checkGreaterOrEqualATVersion, isExpressionValue } from '../utils'
import { addColumns, removeColumn } from '../utils/schemas'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SINGLE_OUTPUT_NAME, SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

const modes: Record<string, string[]> = {}
modes[IsolinesProviders.Here] = ['Walk', 'Car', 'Bike', 'Scooter', 'Truck', 'Taxi', 'Bus', 'Private bus']
modes[IsolinesProviders.Mapbox] = ['Walk', 'Car', 'Bike']
modes[IsolinesProviders.Mock] = ['Walk', 'Car']
modes[IsolinesProviders.TomTom] = ['Car', 'Motorbike', 'Truck', 'Taxi', 'Bus', 'Van']
modes[IsolinesProviders.TravelTime] = ['Walk', 'Car', 'Bike', 'Public transport', 'Coach', 'Bus', 'Train', 'Ferry']

const providerExtraOptions: Record<string, WorkflowComponentInput[]> = {}

providerExtraOptions[IsolinesProviders.Here] = [
  {
    name: 'here_arrival_time',
    type: WorkflowComponentParamType.String,
    title: 'Arrival time',
    optional: true,
    placeholder: 'YYYY-MM-DDThh:mm:ss'
  },
  {
    name: 'here_departure_time',
    type: WorkflowComponentParamType.String,
    title: 'Departure time',
    optional: true,
    placeholder: 'YYYY-MM-DDThh:mm:ss'
  },
  {
    name: 'here_optimize_for',
    type: WorkflowComponentParamType.Selection,
    title: 'Optimize for',
    options: ['balanced', 'quality', 'performance'],
    default: 'balanced'
  },
  {
    name: 'here_routing_mode',
    type: WorkflowComponentParamType.Selection,
    title: 'Routing mode',
    options: ['fast', 'short'],
    default: 'fast'
  },
  {
    name: 'here_origin_radius',
    type: WorkflowComponentParamType.Number,
    title: 'Origin radius (m)',
    min: 0,
    max: 200,
    mode: WorkflowComponentParameterMode.Slider,
    default: 0,
    allowExpressions: false
  }
]

providerExtraOptions[IsolinesProviders.TomTom] = [
  {
    name: 'tomtom_departure_time',
    type: WorkflowComponentParamType.String,
    title: 'Departure time',
    optional: true,
    placeholder: 'YYYY-MM-DDThh:mm:ss'
  },
  {
    name: 'tomtom_traffic',
    type: WorkflowComponentParamType.Boolean,
    title: 'Use traffic information if available',
    default: true
  }
]

providerExtraOptions[IsolinesProviders.TravelTime] = [
  {
    name: 'traveltime_departure_time',
    type: WorkflowComponentParamType.String,
    title: 'Departure time',
    optional: true,
    placeholder: 'YYYY-MM-DDThh:mm:ss'
  },
  {
    name: 'traveltime_single_shape',
    type: WorkflowComponentParamType.Boolean,
    title: 'Single Shape',
    default: true
  }
]

class Isolines extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.isolines',
        group: 'Spatial Constructors',
        title: 'Create Isolines',
        description: `
      This component computes isolines.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'mode',
          type: WorkflowComponentParamType.Selection,
          title: 'Mode',
          options: ['walk', 'car']
        },
        {
          name: 'type',
          type: WorkflowComponentParamType.Selection,
          title: 'Type of range',
          options: ['time', 'distance']
        },
        {
          name: 'range',
          type: WorkflowComponentParamType.Number,
          title: 'Range value (seconds/meters)',
          min: 0
        },
        {
          name: 'customoptions',
          type: WorkflowComponentParamType.String,
          title: 'Isolines options',
          default: '',
          optional: true,
          mode: WorkflowComponentParameterMode.Multiline,
          allowExpressions: false,
          advanced: true
        }
      ]
    )
  }

  adaptComponent(provider: Provider, envVars: WorkflowEnvVars): void {
    if (envVars.mapProvider && envVars.mapProvider.isolines) {
      if (modes[envVars.mapProvider.isolines]) {
        const modeInput = this.inputs.find((input) => input.name === 'mode')
        if (modeInput) {
          modeInput.options = modes[envVars.mapProvider.isolines]
        }
      }

      if (providerExtraOptions[envVars.mapProvider.isolines]) {
        this.inputs.push(...providerExtraOptions[envVars.mapProvider.isolines])
      }
    }
  }

  keepOriginalGeomVersion(provider: Provider, envVars: WorkflowEnvVars): boolean {
    let keepOriginalGeomVersion = ''
    switch (provider) {
      case Provider.BigQuery:
        keepOriginalGeomVersion = '1.16.0'
        break
      case Provider.Snowflake:
        keepOriginalGeomVersion = '1.14.0'
        break
      case Provider.Redshift:
        keepOriginalGeomVersion = '1.8.0'
        break
    }
    return (
      envVars.isCartoDW ||
      checkGreaterOrEqualATVersion(keepOriginalGeomVersion, envVars.analyticsToolboxVersion) ||
      provider === Provider.DatabricksRest
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    if (
      connection.type !== Provider.BigQuery &&
      connection.type !== Provider.Snowflake &&
      connection.type !== Provider.Redshift &&
      connection.type !== Provider.DatabricksRest
    ) {
      return null
    }
    const preparedParams = this._prepareParameters(params)

    const transportMode = (preparedParams.mode as string).replace(' ', '_').toLowerCase()

    const extraOptions = {}

    // Here extra options
    if (preparedParams.here_arrival_time != null) {
      extraOptions['arrivalTime'] = preparedParams.here_arrival_time
    }

    if (preparedParams.here_departure_time != null) {
      extraOptions['departureTime'] = preparedParams.here_departure_time
    }

    if (preparedParams.here_optimize_for != null) {
      extraOptions['optimizeFor'] = preparedParams.here_optimize_for
    }

    if (preparedParams.here_routing_mode != null) {
      extraOptions['routingMode'] = preparedParams.here_routing_mode
    }

    if (preparedParams.here_origin_radius != null) {
      extraOptions['radius'] = preparedParams.here_origin_radius
    }

    // TomTom extra options
    if (preparedParams.tomtom_departure_time != null) {
      extraOptions['departAt'] = preparedParams.tomtom_departure_time
    }

    if (preparedParams.tomtom_traffic != null) {
      extraOptions['traffic'] = preparedParams.tomtom_traffic
    }

    // TravelTime extra options
    if (preparedParams.traveltime_departure_time != null) {
      extraOptions['departure_time'] = preparedParams.traveltime_departure_time
    }

    if (preparedParams.traveltime_single_shape) {
      extraOptions['polygons_filter'] = { limit: 1 }
    }

    // Combine custom and extra options priorizing customoptions
    let options = preparedParams.customoptions
      ? {
          ...extraOptions,
          ...JSON.parse(preparedParams.customoptions as string)
        }
      : extraOptions

    if (this.keepOriginalGeomVersion(connection.type, envVars)) {
      options['carto_keep_orig_geom'] = true
    }
    const nullOptions = connection.type === Provider.DatabricksRest ? "''" : 'NULL'
    options = Object.keys(options).length === 0 ? nullOptions : `'${JSON.stringify(options)}'`
    const dryRun = codeType === WorkflowCodeType.DryRun
    const storedProcedure = codeType === WorkflowCodeType.StoredProcedure
    const tablename = connection.getOutputTableName(workflow.id, componentName, params, SINGLE_OUTPUT_NAME, codeType)
    const temptablename = connection.getOutputTableName(workflow.id, componentName, params, 'temp', codeType)
    let query = ''
    if (connection.type === Provider.BigQuery) {
      query = dedent`
        ${
          storedProcedure
            ? dedent`DECLARE tempOutputTable STRING DEFAULT '${connection.quoteName(
                connection.tempLocation
              )}.' || REPLACE(GENERATE_UUID(), '-', '_');
               BEGIN
               `
            : ''
        }
        CALL ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.CREATE_ISOLINES(
          '${envVars.apiBaseUrl}',
          '${envVars.accessToken}',
          'SELECT * FROM ${connection.quoteName(preparedParams.source as string)}${dryRun ? ' WHERE 1=0' : ''}',
          ${storedProcedure ? 'tempOutputTable' : `'${tablename}'`},
          '${preparedParams.geo}',
          '${transportMode}',
          ${connection.castValue(
            `${preparedParams.range}`,
            connection.numericTypeToDataProviderType(NumericType.Integer)
          )},
          '${preparedParams.type}',
          ${options});
        ${
          storedProcedure
            ? dedent`
          EXECUTE IMMEDIATE '''
            CREATE TEMPORARY TABLE ${connection.quoteName(tablename)} AS
              SELECT *
              FROM ''' || tempOutputTable;
          END;`
            : ''
        }
        `
    } else if (connection.type === Provider.Snowflake) {
      const ldsParams = checkGreaterOrEqualATVersion('1.6.0', envVars.analyticsToolboxVersion)
        ? `,'${envVars.apiBaseUrl}','${envVars.accessToken}'`
        : ''
      query = dedent`
        ${
          storedProcedure
            ? dedent`DECLARE
                tempOutputTable TEXT DEFAULT '${connection.quoteName(
                  connection.tempLocation
                )}.outputtable' || REPLACE(UUID_STRING(), '-', '_');
              BEGIN
              `
            : ''
        }
        CALL ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.CREATE_ISOLINES(
          'SELECT * FROM ${connection.quoteName(preparedParams.source as string)}${dryRun ? ' WHERE 1=0' : ''}',
          ${storedProcedure ? 'tempOutputTable' : `'${tablename}'`},
          '${preparedParams.geo}',
          '${transportMode}',
          ${connection.castValue(
            `${preparedParams.range}`,
            connection.numericTypeToDataProviderType(NumericType.Integer)
          )},
          '${preparedParams.type}',
          ${options}${ldsParams}
        );
        ${
          storedProcedure
            ? dedent`
              EXECUTE IMMEDIATE '
                CREATE TEMPORARY TABLE ${connection.quoteName(tablename)} AS
                  SELECT *
                  FROM ' || tempOutputTable;
              END;`
            : ''
        }
        `
    } else if (connection.type === Provider.Redshift) {
      const source = dryRun ? temptablename : (params.source as string)
      const ldsParams = checkGreaterOrEqualATVersion('1.3.0', envVars.analyticsToolboxVersion)
        ? `,'${envVars.apiBaseUrl}','${envVars.accessToken}'`
        : ''
      query = dedent`
          ${
            storedProcedure
              ? dedent`DECLARE
                    outputTableName VARCHAR(MAX) DEFAULT '${connection.quoteName(
                      connection.tempLocation
                    )}.outputtable' || REPLACE(EXTRACT(EPOCH FROM CURRENT_TIME)::VARCHAR, '.', '');
                  BEGIN
                  `
              : dedent`DECLARE
                  outputTableName VARCHAR(MAX):= '${tablename}';
                BEGIN`
          }
          CALL ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.CREATE_ISOLINES(
            'SELECT * FROM ${connection.quoteName(preparedParams.source as string)}${dryRun ? ' WHERE 1=0' : ''}',
              outputTableName,
              '${preparedParams.geo}',
              '${transportMode}',
              ${connection.castValue(
                `${preparedParams.range}`,
                connection.numericTypeToDataProviderType(NumericType.Integer)
              )},
              '${preparedParams.type}',
              ${options}${ldsParams}
          );
          ${
            storedProcedure
              ? dedent`
                EXECUTE '
                  CREATE TEMPORARY TABLE ${connection.quoteName(tablename)} AS
                    SELECT *
                    FROM ' || outputTableName;`
              : ''
          }
          END;
          `
    } else if (connection.type === Provider.DatabricksRest) {
      query = `
        spark.sql('''DROP TABLE IF EXISTS ${connection.quoteName(temptablename)}''')
        spark.read.format(
          "com.carto.analytics.toolbox.stored.procedures.ATStoredProcedureDatasource"
        ).option(
            "sql",
            """
            CALL_CARTO carto_un.carto.CREATE_ISOLINES(
              '${envVars.apiBaseUrl}',
              '${envVars.accessToken}',
              'SELECT * FROM ${connection.quoteName(preparedParams.source as string)}${dryRun ? ' WHERE 1=0' : ''}',
              '${connection.quoteName(temptablename)}',
              '${preparedParams.geo}',
              '${transportMode}',
              ${connection.castValue(
                `${preparedParams.range}`,
                connection.numericTypeToDataProviderType(NumericType.Integer)
              )},
              '${preparedParams.type}',
              ${options}
              );
            """
          ).load().collect();
        ${(connection as DBricksConnection).wrapCodeInPrepareForVisualizationBlock(
          connection.quoteName(temptablename),
          connection.quoteName(tablename),
          'geom', // CREATE_ISOLINES generates a column named 'geom'
          codeType
        )}
        `
    }
    query = connection.wrapCodeInIfTableExistsBlock(query, [tablename], codeType)

    const outputs: Record<string, string> = {}
    outputs[SINGLE_OUTPUT_NAME] = tablename
    return {
      outputs,
      query
    }
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams) {
    const problems = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })
    if (resolvedParamValues.customoptions && !isExpressionValue(params.customoptions as WorkflowComponentValue)) {
      try {
        JSON.parse(resolvedParamValues.customoptions as string)
      } catch (e) {
        problems.push({
          parameter: 'customoptions',
          type: WorkflowParameterValueErrorType.WrongValue,
          message: 'Invalid JSON string'
        })
      }
    }
    return problems
  }

  getRequiredGrants(): GrantsAndAllowedApis {
    return {
      grants: [],
      apis: [TokenAllowedApis.lds]
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getVersion(provider: Provider): string {
    return '2'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params, connection, envVars }: GetOutputSchemaParams): TableSchema {
    return addColumns(removeColumn(columns.source, params.geo as string), [
      {
        name: 'geom',
        type: connection.getGeoDataType()
      },
      {
        name: 'carto_isoline_metadata',
        type: ColumnDataType.String
      },
      ...(this.keepOriginalGeomVersion(connection.type, envVars)
        ? [
            {
              name: 'orig_geom',
              type: connection.getGeoDataType()
            }
          ]
        : []),
      ...(connection.type === Provider.DatabricksRest ? (connection as DBricksConnection).BOUNDS_COLUMNS : [])
    ])
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.Redshift, Provider.DatabricksRest]
  }
}

export { Isolines }
